import { useEffect, useRef, useState, useCallback } from 'react'
import { logError } from '@/utils/errorUtils'
import { useMedplum } from '@medplum/react'
import {
  Modal,
  Box,
  ActionIcon,
  Select,
  TextInput,
  InputBase,
  Autocomplete,
  Loader,
  MultiSelect,
  Collapse,
} from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'

import { IconX, IconChevronDown, IconMapPin } from '@tabler/icons-react'
import { useForm, type FormErrors } from '@mantine/form'
import { getValidatedAddressIndexes, validateAddresses } from './address-validation'
import {
  createUpdatePatient,
  getSSNFromPatient,
  getMothersMaidenNameFromPatient,
  getPreferredLanguageFromPatient,
  getBirthSexFromPatient,
  getRaceFromPatient,
  ETHNICITY_OPTIONS,
  RACE_OPTIONS,
  LANGUAGE_OPTIONS,
  getEthnicityFromPatient,
  getSecondEthnicityFromPatient,
} from './service'
import type { PatientData } from './service'
import { Patient } from '@medplum/fhirtypes'
import { USPhoneInput } from '@/components/ui/USPhoneInput'
import { IMaskInput } from 'react-imask'
import { ManageCoverages } from '@/components/ui'
import { USDateInput } from '@/components/ui/USDateInput'
import type { InsuranceRank } from '@/types/insuranceTypes'
import { showNotification } from '@mantine/notifications'
import { states } from '@/utils/states.ts'
import { useDebouncedValue } from '@mantine/hooks'
import { useMiddlewareClient } from '@/hooks'
import { useClient } from '@/hooks/useClient'
import { FormSectionHeader } from '@/components/ui/FormSectionHeader'
import {
  CANCEL_BTN,
  FIELD_INPUT,
  FIELD_LABEL,
  GHOST_BTN,
  GROUP_LABEL,
  MODAL_CONTENT,
  MODAL_FOOTER,
  FOOTER_HINT,
  SUBMIT_BTN,
} from '@/components/ui/formModal.tokens'

const CHEVRON = <IconChevronDown size={14} className="opacity-60" />

interface AddressSuggestion {
  street_line: string
  secondary?: string
  city: string
  state: string
  zipcode?: string
  entries?: number
}

function getAddressSuggestionValue(suggestion: AddressSuggestion): string {
  return `${suggestion.street_line}|${suggestion.city}|${suggestion.state}|${suggestion.zipcode ?? ''}`
}

function getAddressSuggestionLabel(suggestion: AddressSuggestion): string {
  return [
    suggestion.street_line,
    suggestion.secondary,
    suggestion.city,
    suggestion.state,
    suggestion.zipcode,
  ]
    .filter(Boolean)
    .join(', ')
}

type LabeledFieldProps = {
  label?: string
  optional?: boolean
  placeholder?: string
} & React.ComponentProps<typeof TextInput>

function LabeledField({ label = '', placeholder = '', ...props }: LabeledFieldProps) {
  if (!props.value) {
    props.value = ''
  }
  return (
    <TextInput
      label={label}
      key={label}
      name={label}
      placeholder={placeholder}
      classNames={{
        root: 'w-full',
        label: FIELD_LABEL,
        input: FIELD_INPUT,
      }}
      {...props}
    />
  )
}

function AddressAutocomplete({
  label,
  value,
  onChange,
  onAddressSelect,
  withAsterisk = false,
  placeholder = '123 Highland Rd',
  ...props
}: {
  label: string
  value: string
  onChange: (value: string) => void
  onAddressSelect: (suggestion: AddressSuggestion) => void
  withAsterisk?: boolean
  placeholder?: string
}) {
  const middleware = useMiddlewareClient()
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([])
  const [loading, setLoading] = useState(false)
  const [debouncedValue] = useDebouncedValue(value, 300)
  const isSelectingRef = useRef(false)
  const shouldSearchRef = useRef(false)

  const searchAddresses = useCallback(
    async (query: string) => {
      if (!query || query.length < 3 || query.length > 100) {
        setSuggestions([])
        return
      }

      setLoading(true)
      try {
        const client = middleware as unknown as {
          get: (url: string) => Promise<{ suggestions: AddressSuggestion[] }>
        }
        const data = await client.get(`/address/autocomplete?query=${encodeURIComponent(query)}`)
        setSuggestions(data?.suggestions ?? [])
      } catch (error) {
        logError('patient-modal', 'Error fetching address suggestions', error)
        setSuggestions([])
      } finally {
        setLoading(false)
      }
    },
    [middleware],
  )

  useEffect(() => {
    if (shouldSearchRef.current && !isSelectingRef.current) {
      searchAddresses(debouncedValue)
      shouldSearchRef.current = false
    }
  }, [debouncedValue, searchAddresses])

  const handleInputChange = (newValue: string) => {
    if (isSelectingRef.current) {
      isSelectingRef.current = false
      return
    }
    shouldSearchRef.current = true
    onChange(newValue)
  }

  const handleSelectSuggestion = (selectedValue: string) => {
    isSelectingRef.current = true
    const suggestion = suggestions.find((s) => getAddressSuggestionValue(s) === selectedValue)
    if (suggestion) {
      onAddressSelect(suggestion)
      setSuggestions([])
    }
  }

  const autocompleteOptions = Array.from(
    new Map(
      suggestions.map((s) => {
        const optionValue = getAddressSuggestionValue(s)
        const optionLabel = getAddressSuggestionLabel(s)

        return [
          optionValue,
          {
            value: optionValue,
            label: optionLabel,
          },
        ]
      }),
    ).values(),
  )

  return (
    <Autocomplete
      label={label}
      classNames={{ root: 'w-full', label: FIELD_LABEL, input: FIELD_INPUT }}
      placeholder={placeholder}
      value={value}
      filter={(input) => input.options}
      onChange={handleInputChange}
      onOptionSubmit={handleSelectSuggestion}
      data={autocompleteOptions}
      withAsterisk={withAsterisk}
      rightSection={
        loading ? <Loader size={16} /> : <IconMapPin size={16} className="text-gray-400" />
      }
      {...props}
    />
  )
}
// Exported so a caller building a `prefill` can name the shape it is filling.
export type PatientFormValues = PatientData

const DOB_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/

function validateDateOfBirth(value = ''): string | null {
  const match = value.match(DOB_PATTERN)
  if (!match) {
    return 'Invalid date of birth'
  }

  const [, yearString, monthString, dayString] = match
  const month = Number(monthString)
  const day = Number(dayString)
  const year = Number(yearString)

  if (year < 1900) {
    return 'Invalid date of birth'
  }

  const date = new Date(year, month - 1, day)
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return 'Invalid date of birth'
  }

  const today = new Date()
  today.setHours(0, 0, 0, 0)

  return date > today ? 'Invalid date of birth' : null
}

function validateSSN(value?: string | null): string | null {
  const pureSSN = value?.replaceAll('-', '') ?? ''
  return pureSSN.length > 0 && pureSSN.length < 9 ? 'Invalid SSN, ssn must be 9 digits' : null
}

const getPatientValues = (patient?: Patient) => {
  const dob = patient?.birthDate || ''

  const addresses: PatientFormValues['addresses'] = patient?.address?.map((addr) => ({
    line: addr.line?.[0] || '',
    city: addr.city || '',
    state: addr.state || '',
    postalCode: addr.postalCode || '',
    use: addr.use,
  })) || [
    {
      line: '',
      city: '',
      state: '',
      postalCode: '',
      use: undefined,
    },
  ]

  return {
    firstName: patient?.name?.[0]?.given?.[0] || '',
    lastName: patient?.name?.[0]?.family || '',
    gender: patient?.gender || '',
    dob,
    prefix: patient?.name?.[0]?.prefix?.[0] || '',
    middleName: patient?.name?.[0]?.given?.[1] || '',
    email: patient?.telecom?.find((t) => t.system === 'email')?.value || '',
    phone: patient?.telecom?.find((t) => t.system === 'phone')?.value || '',
    addresses,
    sexAtBirth: patient ? getBirthSexFromPatient(patient) : '',
    race: patient ? getRaceFromPatient(patient)[0] : '',
    secondRace: patient ? getRaceFromPatient(patient)[1] : '',
    ethnicity: patient ? getEthnicityFromPatient(patient) : [],
    secondEthnicity: patient ? getSecondEthnicityFromPatient(patient) : [],
    mothersMaidenName: patient ? getMothersMaidenNameFromPatient(patient) : '',
    ssn: patient ? getSSNFromPatient(patient) : '',
    preferredLanguage: patient ? getPreferredLanguageFromPatient(patient) : '',
  } satisfies PatientData
}

export const CreateUpdatePatientModal = ({
  isOpen,
  onClose,
  successCallback,
  onInsuranceChanged,
  patient,
  prefill,
  initialInsuranceRankToEdit,
}: {
  isOpen: boolean
  onClose: () => void
  successCallback: (patient: Patient) => void
  /**
   * A coverage was saved or a patient insurance request closed in the insurance section,
   * whether or not the patient form is saved.
   */
  onInsuranceChanged?: () => void
  patient?: Patient
  /**
   * Seed values for a **new** patient — for importing demographics from somewhere else (a Zocdoc
   * booking, say) so they don't have to be retyped.
   *
   * Distinct from `patient` on purpose: `patient` means "edit this existing chart", which skips
   * the invitation and calls `updateResource`. A prefill still creates and still invites; it only
   * saves typing. Ignored when `patient` is given, since editing a real chart must show that
   * chart's own values.
   */
  prefill?: Partial<PatientFormValues>
  initialInsuranceRankToEdit?: InsuranceRank
}) => {
  const medplum = useMedplum()
  const client = useClient()
  const savePendingCoveragesRef = useRef<((patientId: string) => Promise<void>) | null>(null)
  // The ref blocks a second click before React re-renders; the state only drives `disabled`.
  const submittingRef = useRef(false)
  const [submitting, setSubmitting] = useState(false)
  const [isExtraDemographicsOpen, { toggle, open: openExtraDemographics }] = useDisclosure(false)
  const extraDemographicsFormRef = useRef<HTMLButtonElement | null>(null)

  const form = useForm<PatientFormValues>({
    // main's whole-form `validate` below, with our `prefill` seeding kept: an existing chart
    // reads from the patient, a new one starts from the defaults overlaid with whatever the
    // caller prefilled (the Zocdoc details popup builds one from the booking's participant).
    initialValues: patient
      ? getPatientValues(patient)
      : { ...getPatientValues(undefined), ...prefill },
    // Whole-form function (not the declarative per-element `addresses: {...}` rules)
    // so the address rules can see each row's sibling `use` field and skip the
    // soft-deleted ones — same shape patient-web's Profile.tsx already uses.
    validate: (values): FormErrors => ({
      firstName:
        (values.firstName ?? '').length < 2 ? 'First name must have at least 2 letters' : null,
      lastName:
        (values.lastName ?? '').length < 2 ? 'Last name must have at least 2 letters' : null,
      email: /\S+@\S+\.\S+/.test(values.email ?? '') ? null : 'Invalid email address',
      phone:
        !values.phone || /^\+?[1-9]\d{7,14}$/.test(values.phone) ? null : 'Invalid phone number',
      gender: values.gender ? null : 'Please select a gender',
      ssn: validateSSN(values.ssn),
      dob: validateDateOfBirth(values.dob),
      ...validateAddresses(values.addresses),
    }),
  })

  useEffect(() => {
    if (isOpen) {
      form.reset()
      if (patient) {
        form.setValues(getPatientValues(patient))
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, patient])

  useEffect(() => {
    if (Object.keys(form.errors).length > 0) {
      if (form.errors.ssn) {
        openExtraDemographics()
        extraDemographicsFormRef.current?.scrollIntoView({ behavior: 'smooth' })
      }
    }
  }, [form.errors, openExtraDemographics])

  const handleFormSubmit = async (values: PatientFormValues) => {
    if (submittingRef.current) return
    let hasInvalidZip = false
    // Same row selection as the form validator: soft-deleted rows are not rendered
    // and an untouched optional row is dropped on save, so neither may block submit.
    getValidatedAddressIndexes(values.addresses).forEach((idx) => {
      const digits = (values.addresses[idx].postalCode || '').replace(/\D/g, '')
      if (!(digits.length === 5 || digits.length === 9)) {
        form.setFieldError(`addresses.${idx}.postalCode`, 'ZIP code must be 5 or 9 digits')
        hasInvalidZip = true
      }
    })
    if (hasInvalidZip) {
      showNotification({
        color: 'red',
        title: 'Invalid ZIP Code',
        message: 'Each ZIP code must be 5 or 9 digits.',
      })
      return
    }
    submittingRef.current = true
    setSubmitting(true)
    try {
      const result = await createUpdatePatient(medplum, values, patient, client)

      if (result) {
        // Drafted coverages belong to the patient being created. When the data matched an
        // existing chart, that chart opens untouched and the drafts are discarded with the modal.
        if (!patient && !result.existing) {
          await savePendingCoveragesRef.current?.(result.patient.id as string)
        }
        successCallback(result.patient)
        closeModal()
      }
    } catch (error) {
      logError('patient-modal', `Error ${patient ? 'updating' : 'creating'} patient`, error)
    } finally {
      submittingRef.current = false
      setSubmitting(false)
    }
  }

  const addAddress = () => {
    form.insertListItem('addresses', {
      line: '',
      city: '',
      state: '',
      postalCode: '',
    })
  }

  const removeAddress = (index: number) => {
    const activeAddressesCount = form.values.addresses.filter(
      (address) => address.use !== 'old',
    ).length

    if (activeAddressesCount > 1) {
      form.setFieldValue(`addresses.${index}.use`, 'old')
    }
  }

  const closeModal = () => {
    form.reset()
    savePendingCoveragesRef.current = null
    onClose()
  }

  const handleCoverageSaved = () => {
    onInsuranceChanged?.()
    if (patient && initialInsuranceRankToEdit) {
      successCallback(patient)
      closeModal()
    }
  }

  const visibleAddressIndexes = form.values.addresses.reduce<number[]>((acc, address, index) => {
    if (address.use !== 'old') {
      acc.push(index)
    }

    return acc
  }, [])

  return (
    <Modal
      className="addpatient-modal"
      opened={isOpen}
      onClose={closeModal}
      centered
      withCloseButton={false}
      classNames={{
        content: MODAL_CONTENT,
        body: '!p-0',
      }}
      radius={18}
      size="680px"
    >
      <form
        onSubmit={form.onSubmit((values) => handleFormSubmit(values))}
        className="flex max-h-[86vh] flex-col"
      >
        {/* Header — pinned above the scrolling form body. */}
        <div className="flex items-start gap-2.5 px-6 pt-5 pb-4">
          <div className="flex-1">
            <div className="text-[17px] font-extrabold tracking-tight text-[#1F2733]">
              {patient ? 'Update patient' : 'Create patient'}
            </div>
            <div className="text-[12.5px] text-[#8A929C]">
              Required fields are marked <span className="text-[#E5533D]">*</span>
            </div>
          </div>
          <button
            type="button"
            title="Close"
            onClick={closeModal}
            className="flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-[7px] text-[#8A929C] hover:bg-[#F2F4F6] hover:text-[#1F2733]"
          >
            <IconX size={17} stroke={2.1} />
          </button>
        </div>

        {/* Scrolling body — the footer stays visible on long forms. */}
        <div className="min-h-0 flex-1 overflow-y-auto border-t border-[#EEF0F2] px-6 py-5">
          <FormSectionHeader title="Demographics" />
          <Box className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <Select
              label="Prefix"
              name="prefix"
              placeholder="Select"
              data={[
                { value: 'Mr.', label: 'Mr.' },
                { value: 'Mrs.', label: 'Mrs.' },
                { value: 'Ms.', label: 'Ms.' },
                { value: 'Miss.', label: 'Miss.' },
              ]}
              allowDeselect
              rightSection={CHEVRON}
              rightSectionWidth={30}
              {...form.getInputProps('prefix')}
              classNames={{
                root: 'w-full',
                label: FIELD_LABEL,
                input: FIELD_INPUT,
              }}
            />
            <LabeledField
              label="First name"
              placeholder="First name"
              name="firstName"
              withAsterisk
              {...form.getInputProps('firstName')}
            />
            <LabeledField
              label="Last name"
              placeholder="Last name"
              name="lastName"
              withAsterisk
              {...form.getInputProps('lastName')}
            />
            <LabeledField
              label="Middle name (optional)"
              name="middleName"
              placeholder="Middle name"
              {...form.getInputProps('middleName')}
            />
            <USDateInput
              label="Date of birth"
              key={'dob'}
              name={'dob'}
              withAsterisk
              value={form.values.dob || null}
              error={form.errors.dob}
              onFocus={() => form.setTouched((touched) => ({ ...touched, dob: true }))}
              onChange={(value) => form.setFieldValue('dob', value ?? '')}
              onBlur={() => form.validateField('dob')}
              classNames={{
                root: 'w-full',
                label: FIELD_LABEL,
                input: FIELD_INPUT,
              }}
            />
            <Select
              label="Gender"
              placeholder="Select gender"
              withAsterisk
              data={[
                { value: 'male', label: 'Male' },
                { value: 'female', label: 'Female' },
                { value: 'other', label: 'Other' },
                { value: 'unknown', label: 'Unknown' },
              ]}
              rightSection={CHEVRON}
              rightSectionWidth={30}
              {...form.getInputProps('gender')}
              classNames={{
                root: 'w-full',
                label: FIELD_LABEL,
                input: FIELD_INPUT,
              }}
            />
          </Box>
          <button
            type="button"
            className={`mt-4 ${GHOST_BTN}`}
            onClick={toggle}
            ref={extraDemographicsFormRef}
          >
            {isExtraDemographicsOpen ? 'Hide extra demographics' : '+ Show extra demographics'}
          </button>
          <Collapse in={isExtraDemographicsOpen} className="w-full">
            <Box className="mt-4 grid grid-cols-2 gap-4">
              <Select
                label="Sex at Birth"
                placeholder="Select sex"
                classNames={{
                  root: 'w-full',
                  label: FIELD_LABEL,
                  input: FIELD_INPUT,
                }}
                data={[
                  { value: 'male', label: 'Male' },
                  { value: 'female', label: 'Female' },
                ]}
                rightSection={CHEVRON}
                rightSectionWidth={30}
                {...form.getInputProps('sexAtBirth')}
              />
              <LabeledField
                label="Mother’s Maiden name"
                name="mothersMaidenName"
                placeholder="Enter Mother’s Maiden name"
                {...form.getInputProps('mothersMaidenName')}
              />
            </Box>
            <Box className="mt-4 grid gap-4">
              <MultiSelect
                label="Ethnicity"
                name="ethnicity"
                defaultValue={patient ? getEthnicityFromPatient(patient) : []}
                placeholder={form.getValues()?.ethnicity?.length ? '' : 'Pick value'}
                rightSection={' '}
                data={ETHNICITY_OPTIONS.map((group) => ({
                  group: group.group,
                  items: group.items.map((option) => ({
                    label: option.name,
                    value: option.code,
                  })),
                }))}
                onChange={(value) => form.setFieldValue('ethnicity', value)}
                classNames={{
                  groupLabel: GROUP_LABEL,
                  root: 'w-full',
                  label: FIELD_LABEL,
                  input: FIELD_INPUT,
                }}
              />
              <MultiSelect
                label="Second ethnicity"
                name="secondEthnicity"
                onChange={(value) => {
                  form.setFieldValue('secondEthnicity', value)
                }}
                placeholder={form.getValues()?.secondEthnicity?.length ? '' : 'Pick value'}
                classNames={{
                  groupLabel: GROUP_LABEL,
                  root: 'w-full',
                  label: FIELD_LABEL,
                  input: FIELD_INPUT,
                }}
                rightSection={' '}
                defaultValue={patient ? getSecondEthnicityFromPatient(patient) : []}
                data={ETHNICITY_OPTIONS.map((group) => ({
                  group: group.group,
                  items: group.items.map((option) => ({
                    label: option.name,
                    value: option.code,
                    // value: JSON.stringify({...option, system: option?.system || 'https://behuman.health/fhir/CodeSystem/behuman-ethnicity'})
                  })),
                }))}
              />

              <Select
                label="Race"
                placeholder="Select race"
                name="race"
                data={RACE_OPTIONS.map((option) => ({
                  label: option.name,
                  value: option.code,
                }))}
                rightSection={CHEVRON}
                rightSectionWidth={30}
                {...form.getInputProps('race')}
                classNames={{
                  // root: 'w-full',
                  label: FIELD_LABEL,
                  input: FIELD_INPUT,
                }}
              />
              <Select
                label="Second race"
                placeholder="Select race"
                data={RACE_OPTIONS.map((option) => ({
                  label: option.name,
                  value: option.code,
                }))}
                rightSection={CHEVRON}
                rightSectionWidth={30}
                {...form.getInputProps('secondRace')}
                classNames={{
                  root: 'w-full',
                  label: FIELD_LABEL,
                  input: FIELD_INPUT,
                }}
              />
            </Box>
            <Box className="mt-4 grid grid-cols-2 gap-4">
              <InputBase
                label={'SSN'}
                placeholder="XXX-XX-XXXX"
                component={IMaskInput}
                mask={'000-00-0000'}
                name="ssn"
                classNames={{
                  label: FIELD_LABEL,
                  input: FIELD_INPUT,
                }}
                {...form.getInputProps(`ssn`)}
              />
              <Select
                label="Preferred language"
                placeholder="Select language"
                classNames={{
                  root: 'w-full',
                  label: FIELD_LABEL,
                  input: FIELD_INPUT,
                }}
                data={LANGUAGE_OPTIONS.map((lang) => ({
                  value: JSON.stringify(lang),
                  label: lang.display,
                }))}
                rightSection={CHEVRON}
                rightSectionWidth={30}
                {...form.getInputProps('preferredLanguage')}
              />
            </Box>
          </Collapse>

          <div className="mt-6">
            <ManageCoverages
              patientId={patient?.id}
              initialInsuranceRank={isOpen ? initialInsuranceRankToEdit : undefined}
              onCoverageSaved={handleCoverageSaved}
              onPatientInsuranceRequestDismissed={onInsuranceChanged}
              onInsuranceModalClose={initialInsuranceRankToEdit ? closeModal : undefined}
              registerSaveCoverages={(save) => {
                savePendingCoveragesRef.current = save
              }}
            />
          </div>

          <div className="mt-6">
            <FormSectionHeader title="Contact info" />
            <Box className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <p className={FIELD_LABEL}>Phone</p>
                <USPhoneInput
                  placeholder="eg: 234 567 8900"
                  value={form.values.phone}
                  disabled={false}
                  onChange={(value) => form.setFieldValue('phone', value || '')}
                  error={form.errors.phone}
                />
              </div>
              <LabeledField
                label="Email"
                name="email"
                withAsterisk
                placeholder="example@example.com"
                {...form.getInputProps('email')}
              />
            </Box>
          </div>

          {/* Dynamic Addresses */}
          <div className="mt-6">
            <FormSectionHeader title="Addresses" />
          </div>
          {visibleAddressIndexes.map((index, visibleIndex) => (
            <Box
              key={index}
              className="mt-4 grid grid-cols-1 gap-4 rounded-xl border border-[#E7E9EC] p-4 sm:grid-cols-2"
            >
              <div className="col-span-2 flex items-center justify-between">
                <h3 className="text-[13px] font-bold text-[#1F2733]">
                  Address {visibleIndex + 1}
                  {visibleIndex === 0 && <span className="ml-1 text-red-500">*</span>}
                </h3>
                {visibleAddressIndexes.length > 1 && (
                  <ActionIcon
                    variant="subtle"
                    color="red"
                    onClick={() => removeAddress(index)}
                    size="sm"
                  >
                    <IconX size={16} />
                  </ActionIcon>
                )}
              </div>
              <AddressAutocomplete
                label="Address line"
                value={form.values.addresses[index]?.line || ''}
                onAddressSelect={(suggestion) => {
                  const addressLine = [suggestion.street_line, suggestion.secondary]
                    .filter(Boolean)
                    .join(' ')
                  const matchingState =
                    states.find(
                      (s) =>
                        s.label.toLowerCase() === suggestion.state.toLowerCase() ||
                        s.value.toLowerCase() === suggestion.state.toLowerCase(),
                    )?.value || suggestion.state
                  form.setFieldValue(`addresses.${index}.line`, addressLine)
                  form.setFieldValue(`addresses.${index}.city`, suggestion.city)
                  form.setFieldValue(`addresses.${index}.state`, matchingState)
                  form.setFieldValue(`addresses.${index}.postalCode`, suggestion.zipcode ?? '')
                }}
                placeholder="123 Highland Rd"
                withAsterisk={visibleIndex === 0}
                {...form.getInputProps(`addresses.${index}.line`)}
                onChange={(value) => form.setFieldValue(`addresses.${index}.line`, value || '')}
              />
              <LabeledField
                label="City"
                name={`addresses.${index}.city`}
                placeholder="City"
                withAsterisk={visibleIndex === 0}
                {...form.getInputProps(`addresses.${index}.city`)}
              />
              <Select
                label="State/Province"
                name={`addresses.${index}.state`}
                placeholder="Select state"
                data={states}
                {...form.getInputProps(`addresses.${index}.state`)}
                required={visibleIndex === 0}
                classNames={{
                  root: 'w-full',
                  label: FIELD_LABEL,
                  input: FIELD_INPUT,
                }}
                rightSection={CHEVRON}
                rightSectionWidth={30}
              />
              <InputBase
                label="ZIP code"
                withAsterisk={visibleIndex === 0}
                name={`addresses.${index}.postalCode`}
                placeholder="00000 or 12345-1234"
                component={IMaskInput}
                mask={[{ mask: '00000' }, { mask: '00000-0000' }]}
                {...form.getInputProps(`addresses.${index}.postalCode`)}
                onBlur={(e) => {
                  const val = e.target.value.replace(/\D/g, '')
                  if (val.length === 9) {
                    form.setFieldValue(
                      `addresses.${index}.postalCode`,
                      `${val.slice(0, 5)}-${val.slice(5)}`,
                    )
                  }
                }}
              />
            </Box>
          ))}

          <button type="button" className={`mt-3 ${GHOST_BTN}`} onClick={addAddress}>
            + Add additional address
          </button>
        </div>

        {/* Footer — pinned, with the insurance reminder from the design. */}
        <div className={MODAL_FOOTER}>
          <span className={FOOTER_HINT}>You can add insurance later from the patient page.</span>
          <button type="button" className={CANCEL_BTN} onClick={closeModal}>
            Cancel
          </button>
          <button type="submit" className={SUBMIT_BTN} disabled={submitting}>
            {submitting
              ? patient
                ? 'Updating…'
                : 'Creating…'
              : patient
                ? 'Update patient'
                : 'Create patient'}
          </button>
        </div>
      </form>
    </Modal>
  )
}

export default CreateUpdatePatientModal
